// src/hooks/useStaff.ts

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import staffService from '../services/staff.service';
import type { StaffResponse } from '../types/api.types';
import type {
  StaffMember,
  CreateStaffPayload,
  UpdateStaffPayload,
  StaffSearchParams,
} from '../types/api.types';

/**
 * Clés de requête pour le personnel
 */
export const STAFF_QUERY_KEYS = {
  all: ['staff'] as const,
  lists: () => [...STAFF_QUERY_KEYS.all, 'list'] as const,
  list: (params?: StaffSearchParams) => [...STAFF_QUERY_KEYS.lists(), params] as const,
  details: () => [...STAFF_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...STAFF_QUERY_KEYS.details(), id] as const,
  stats: () => [...STAFF_QUERY_KEYS.all, 'stats'] as const,
};

/**
 * Hook pour récupérer la liste du personnel
 */
export const useStaff = (params?: StaffSearchParams) => {
  return useQuery<StaffResponse>({
    queryKey: STAFF_QUERY_KEYS.list(params),
    queryFn: async () => {
      const response = await staffService.getStaff(params);
      console.log('Staff API Response:', response);
      return response;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook pour récupérer un membre du personnel par ID
 */
export const useStaffById = (id: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: STAFF_QUERY_KEYS.detail(id),
    queryFn: () => staffService.getStaffById(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

/**
 * Hook pour récupérer les statistiques du personnel
 */
export const useStaffStats = () => {
  return useQuery({
    queryKey: STAFF_QUERY_KEYS.stats(),
    queryFn: () => staffService.getStaffStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

/**
 * Hook pour créer un nouveau membre du personnel
 */
export const useCreateStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateStaffPayload) => staffService.createStaff(payload),
    onSuccess: (newStaff: StaffMember) => {
      // Invalider et refetch la liste du personnel
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.stats() });
      
      // Mettre en cache le nouveau membre du personnel
      queryClient.setQueryData(STAFF_QUERY_KEYS.detail(newStaff.id), newStaff);
      
      message.success('Membre du personnel créé avec succès');
    },
    onError: (error: any) => {
      console.error('Erreur lors de la création du personnel:', error);
      message.error(error.message || 'Échec de la création du personnel');
    },
  });
};

/**
 * Hook pour mettre à jour un membre du personnel
 */
export const useUpdateStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateStaffPayload }) =>
      staffService.updateStaff(id, payload),
    onSuccess: (updatedStaff: StaffMember) => {
      // Mettre à jour le cache
      queryClient.setQueryData(STAFF_QUERY_KEYS.detail(updatedStaff.id), updatedStaff);
      
      // Invalider les listes pour refléter les changements
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.lists() });
      
      message.success('Membre du personnel mis à jour avec succès');
    },
    onError: (error: any) => {
      console.error('Erreur lors de la mise à jour du personnel:', error);
      message.error(error.message || 'Échec de la mise à jour du personnel');
    },
  });
};

/**
 * Hook pour activer un membre du personnel
 */
export const useActivateStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => staffService.activateStaff(id),
    onSuccess: (updatedStaff: StaffMember) => {
      // Mettre à jour le cache
      queryClient.setQueryData(STAFF_QUERY_KEYS.detail(updatedStaff.id), updatedStaff);
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.stats() });
      
      message.success('Membre du personnel activé avec succès');
    },
    onError: (error: any) => {
      console.error('Erreur lors de l\'activation du personnel:', error);
      message.error(error.message || 'Échec de l\'activation du personnel');
    },
  });
};

/**
 * Hook pour désactiver un membre du personnel
 */
export const useDeactivateStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      staffService.deactivateStaff(id, reason),
    onSuccess: (updatedStaff: StaffMember) => {
      // Mettre à jour le cache
      queryClient.setQueryData(STAFF_QUERY_KEYS.detail(updatedStaff.id), updatedStaff);
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.stats() });
      
      message.success('Membre du personnel désactivé avec succès');
    },
    onError: (error: any) => {
      console.error('Erreur lors de la désactivation du personnel:', error);
      message.error(error.message || 'Échec de la désactivation du personnel');
    },
  });
};

/**
 * Hook pour rechercher le personnel
 */
export const useSearchStaff = () => {
  return useMutation({
    mutationFn: ({ 
      searchTerm, 
      filters 
    }: { 
      searchTerm: string; 
      filters?: Partial<StaffSearchParams> 
    }) => staffService.searchStaff(searchTerm, filters),
    onError: (error: any) => {
      console.error('Erreur lors de la recherche du personnel:', error);
      message.error(error.message || 'Échec de la recherche du personnel');
    },
  });
};

/**
 * Hook personnalisé principal qui combine toutes les fonctionnalités
 */
export const useStaffManagement = (initialParams?: StaffSearchParams) => {
  const queryClient = useQueryClient();
  
  const {
    data: staffData,
    isLoading: isLoadingStaff,
    error: staffError,
    refetch: refetchStaff,
  } = useStaff(initialParams);

  const {
    data: statsData,
    isLoading: isLoadingStats,
    error: statsError,
  } = useStaffStats();

  const createStaffMutation = useCreateStaff();
  const updateStaffMutation = useUpdateStaff();
  const activateStaffMutation = useActivateStaff();
  const deactivateStaffMutation = useDeactivateStaff();
  const searchStaffMutation = useSearchStaff();

  // Log the raw staff data for debugging
  React.useEffect(() => {
    console.group('useStaffManagement - Raw Data');
    console.log('Raw staffData:', staffData);
    if (staffData) {
      console.log('staffData.data:', staffData.data);
      console.log('staff array:', staffData.data?.staff || []);
      console.log('pagination:', staffData.pagination);
    }
    console.groupEnd();
  }, [staffData]);

  // Fonction pour rafraîchir toutes les données
  const refreshAll = async () => {
    console.log('Refreshing all staff data...');
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.lists() }),
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.stats() }),
    ]);
  };

  // Fonction pour nettoyer le cache
  const clearCache = () => {
    console.log('Clearing staff cache...');
    queryClient.removeQueries({ queryKey: STAFF_QUERY_KEYS.all });
  };

  // Extract staff and pagination data with null checks
  const staff = React.useMemo(() => {
    const staffList = staffData?.data?.staff || [];
    console.log('Extracted staff list:', staffList);
    return staffList;
  }, [staffData]);

  const pagination = React.useMemo(() => {
    return staffData?.pagination;
  }, [staffData]);

  return {
    // Données
    staff,
    pagination,
    stats: statsData,
    
    // États de chargement
    isLoading: isLoadingStaff,
    isLoadingStats,
    
    // Erreurs
    error: staffError,
    statsError,
    
    // Mutations
    createStaff: createStaffMutation.mutate,
    updateStaff: updateStaffMutation.mutate,
    activateStaff: activateStaffMutation.mutate,
    deactivateStaff: deactivateStaffMutation.mutate,
    searchStaff: searchStaffMutation.mutate,
    
    // États des mutations
    isCreating: createStaffMutation.isPending,
    isUpdating: updateStaffMutation.isPending,
    isActivating: activateStaffMutation.isPending,
    isDeactivating: deactivateStaffMutation.isPending,
    isSearching: searchStaffMutation.isPending,
    
    // Données de recherche
    searchResults: searchStaffMutation.data,
    
    // Fonctions utilitaires
    refetch: refetchStaff,
    refreshAll,
    clearCache,
  };
};

export default useStaffManagement;